import scrollIntoView from 'scroll-into-view-if-needed'
import { Controller } from 'stimulus'
import { getScrollParent } from '~/common/helpers/scroll_parent'
import { notifyDomChanged } from '~/common/helpers/dom_changed'
import { enter, leave } from 'el-transition'
import { getExtension } from '../tiptap/utilities'

import './comments_component.css'
import { isMobileSafari } from '~/common/helpers/user_agent'
import {
  parseSiteCommentHash,
  revealCollapsedAncestors,
  seekAcrossPages,
} from './comments_component_helpers.js'

// Give up on a page that never comes back (network error), so the seek can't hang.
const PAGE_LOAD_TIMEOUT = 15000

const commentReplyCache = {}

export default class extends Controller {
  static targets = [
    'newCommentInsertModeInput',
    'newComment',
    'newReply',
    'comment',
    'composerPlaceholder',
  ]

  static values = {
    content: String,
    // CommentsComponent lazy_composer: the composer and the reply form aren't in the page.
    // They are fetched from here on first use.
    composerUrl: String,
  }

  #composerLoading = false
  #replyLoading = false
  #awaitingReplyEditor = false

  // Ground truth for cancelReply()'s leave().then() guard below, independent of the `hidden`
  // class that same transition is mutating. newReplyTarget always starts hidden (see
  // comments_component.html.erb), so no DOM read is needed at connect time.
  #open = false

  connect() {
    // Notification / copy-link deep links use #site_comment_ID. Comments are
    // cursor-paginated (and sometimes lazy), so the target may not be in the
    // DOM on first paint — load pages until it appears, then scroll.
    this.#scrollToHashedComment()
  }

  disconnect() {
    this.hashSeekAborted = true
  }

  orderChanged(event) {
    const insertMode = event.target.value == 'newest' ? 'prepend' : 'append'
    this.insertMode = insertMode
    this.newCommentInsertModeInputTargets.forEach((t) => (t.value = insertMode))
  }

  // A composer fetched after the order changed must insert the new comment the same way
  newCommentInsertModeInputTargetConnected(input) {
    if (this.insertMode) input.value = this.insertMode
  }

  // The placeholder was clicked: swap in the real composer. It opens itself and focuses the
  // editor as it connects (NewContributionComponent open_on_connect).
  loadComposer() {
    if (this.#composerLoading || !this.hasComposerPlaceholderTarget) return
    this.#composerLoading = true
    const placeholder = this.composerPlaceholderTarget
    placeholder.setAttribute('aria-busy', 'true')
    this.#fetchComposer('new_comment')
      .then((nodes) => {
        placeholder.replaceWith(...nodes)
        // Opening the composer cancels a reply. The fetched composer opens in its connect(), before
        // this controller has bound its formShown->cancelReply action, so cancel here.
        if (this.#open) this.cancelReply()
      })
      .catch((error) => {
        placeholder.removeAttribute('aria-busy') // A network or server error: the next click tries again
        // Refused (signed out or access lost since the page loaded): open the post, as without JS
        if (error.refused)
          window.location.assign(placeholder.querySelector('a').href)
      })
      .finally(() => (this.#composerLoading = false))
  }

  #loadReplyForm() {
    if (this.#replyLoading || !this.composerUrlValue) return
    this.#replyLoading = true
    this.#fetchComposer('reply')
      .then((nodes) => this.element.append(...nodes)) // newReplyTargetConnected opens it
      .catch(() => (this.pendingReplyEvent = null)) // The next Reply tries again
      .finally(() => (this.#replyLoading = false))
  }

  async #fetchComposer(kind) {
    const url = new URL(this.composerUrlValue, window.location.href)
    url.searchParams.set('kind', kind)
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    })
    if (!response.ok || response.redirected)
      throw Object.assign(new Error(`HTTP ${response.status}`), {
        refused: response.redirected || response.status < 500, // a 404, or a redirect to log in
      })
    const template = document.createElement('template')
    template.innerHTML = await response.text()
    const nodes = [...template.content.childNodes]
    // Same as lazy-template-loader: let jQuery behaviors set up the new content once it's in.
    requestAnimationFrame(() => notifyDomChanged(nodes))
    return nodes
  }

  async newReplyTargetConnected(target) {
    if (!this.pendingReplyEvent) return
    // reply() talks to the form's editor right away. A fetched form's editor controller may
    // still be loading (its code loads on demand), so wait for it first. A Reply clicked
    // meanwhile replaces the pending one (reply() checks #awaitingReplyEditor).
    this.#awaitingReplyEditor = true
    const ready = await this.#editorControllerConnected(target)
    this.#awaitingReplyEditor = false
    const event = this.pendingReplyEvent
    this.pendingReplyEvent = null
    if (ready && event) this.reply(event) // Not ready after 10s: dropped, the next Reply waits again
  }

  #editorControllerReady(root) {
    const editor = root.querySelector?.('.js-tiptap-editor-container')
    return (
      !editor ||
      !!this.application?.getControllerForElementAndIdentifier(
        editor,
        'tiptap--editor'
      )
    )
  }

  // Resolves true once the Tiptap controller in `root` has connected (or there's no editor),
  // false if it hasn't after 10 seconds.
  async #editorControllerConnected(root) {
    const editor = root.querySelector?.('.js-tiptap-editor-container')
    if (!editor) return true
    const deadline = performance.now() + 10000
    while (performance.now() < deadline) {
      if (this.#editorControllerReady(root)) return true
      await new Promise((resolve) => requestAnimationFrame(resolve))
    }
    return false
  }

  reply(event) {
    if (this.#awaitingReplyEditor) {
      this.pendingReplyEvent = event
      return
    }
    if (
      this.hasNewReplyTarget &&
      !this.#editorControllerReady(this.newReplyTarget)
    ) {
      this.pendingReplyEvent = event
      this.newReplyTargetConnected(this.newReplyTarget) // Waits for it, then replays
      return
    }
    if (!this.hasNewReplyTarget) {
      this.pendingReplyEvent = event
      this.#loadReplyForm()
      return
    }
    const oldReplyTo = this.newReplyTarget.parentElement.closest('.js-comment')
    // If someone clicks reply on a comment and then clicks reply again on another comment without posting the previous one - save cache in that case
    this.setCache(oldReplyTo)
    let replyTo = event.target.closest('.js-comment')
    const params = event.params

    // Only allow replies up to 2 level
    // Comment                              - Allowed
    //   Reply to comment                   - Allowed
    //      Reply to reply                  - Allowed
    //        Reply to a reply's reply      - Not allowed
    while (
      replyTo.parentElement
        ?.closest('.js-comment')
        ?.parentElement?.closest('.js-comment')
    ) {
      replyTo = replyTo.parentElement.closest('.js-comment')
    }

    // Move form to appropriate place
    replyTo.querySelector('.js-comment-replies').append(this.newReplyTarget)

    // Set correct parent_id
    this.newReplyTarget.querySelector('.js-parent-id-input').value =
      replyTo.dataset.commentId

    this.newReplyTarget
      .querySelector('.js-dropzone-container')
      .classList.add('active')

    this.#open = true

    // Animate in the reply form
    enter(this.newReplyTarget).then(() => {
      const selection = this.getSelection()
      let previouslyTypedContent = null
      // If there is some text selected then don't fetch the previously typed content from cache or else
      // it will never quote the selected text
      if (!selection) {
        previouslyTypedContent = commentReplyCache[replyTo.dataset.commentId]
      }
      const wysiwygEditorContainer = this.newReplyTarget.querySelector(
        '.js-tiptap-editor-container'
      )
      // The editor initializes lazily when visible; the reply form was hidden until now
      wysiwygEditorContainer.dispatchEvent(new CustomEvent('editor:initialize'))
      const wysiwygContentEl =
        wysiwygEditorContainer.querySelector('[contenteditable]')
      let replyContent = []

      // Check if the editor has mentions enabled, could be possible the member director or mentions is disabled on the site or site group
      const extensionName = 'memberMention'
      const mentionExtensionIncluded =
        typeof getExtension(wysiwygContentEl.editor, extensionName) !==
        'undefined'
      const replyToMention =
        params.memberId !== undefined && params.memberName !== undefined
      if (mentionExtensionIncluded && replyToMention) {
        replyContent.push({
          type: extensionName,
          attrs: {
            id: params.memberId,
            label: params.memberName,
          },
        })
        replyContent.push({
          type: 'text',
          text: ' ​', // This has a zero-width space after the normal space so tiptap doesn't cause problems without having to use white-space: pre-wrap
        })
      }

      if (selection) {
        replyContent.push(
          {
            type: 'blockquote',
            content: [
              {
                type: 'paragraph',
                content: [{ type: 'text', text: `${selection}` }],
              },
            ],
          },
          {
            type: 'text',
            text: '\n',
          }
        )
      }

      if (replyContent.length > 0) {
        const event = new CustomEvent('editor:setContent', {
          detail: {
            content: previouslyTypedContent || replyContent,
          },
        })
        wysiwygEditorContainer.dispatchEvent(event)
      }
      // Focus and scroll in the reply form
      scrollIntoView(this.newReplyTarget, {
        behavior: isMobileSafari() ? 'auto' : 'smooth',
        scrollMode: 'if-needed',
        boundary: getScrollParent(this.newReplyTarget),
      })
      wysiwygContentEl.focus({ preventScroll: true })
    })

    // Update the comment connector on the previous comment (if any) and the new comment which we are replying on
    this.updateCommentConnectorToChildren(replyTo)
    this.updateCommentConnectorToChildren(oldReplyTo)
    if (this.hasNewCommentTarget)
      this.dispatch('hideForm', {
        target: this.newCommentTarget,
        prefix: false,
      })
  }

  cancelReply() {
    // Opening the composer cancels a reply, but with lazy_composer there may be no reply form yet
    if (!this.hasNewReplyTarget) return
    const oldReplyTo = this.newReplyTarget.parentElement.closest('.js-comment')
    this.setCache(oldReplyTo)
    this.#open = false
    // leave() only adds the `hidden` class once its ~300ms CSS transition settles, not when it
    // starts. A reply() landing inside that window (possibly moving newReplyTarget under a
    // different comment) clears `hidden` synchronously, but this still-pending leave() would
    // otherwise both re-hide it and run the cleanup below on the reopened form — ripping it out
    // of its new spot and re-caching the wrong comment's connector.
    leave(this.newReplyTarget).then(() => {
      if (this.#open) {
        this.newReplyTarget.classList.remove('hidden')
        return
      }
      this.element.append(this.newReplyTarget)
      this.newReplyTarget
        .querySelector('.js-dropzone-container')
        .classList.remove('active')
      this.updateCommentConnectorToChildren(oldReplyTo)
    })
  }

  commentTargetConnected(target) {
    if (this.hasNewReplyTarget && target != this.newReplyTarget) {
      // New comment was added, cancel reply
      this.cancelReply()
    }
    this.updateCommentConnectorToChildren(
      target.parentElement.closest('.js-comment')
    )
  }

  // having :has() queries would make this so simpler - just with css, no js necessary. Browser support still not good enough (~90%)
  updateCommentConnectorToChildren(comment) {
    if (!comment) return
    comment
      .querySelector('.js-comment-connector-to-children')
      .classList.toggle(
        'hidden',
        comment.querySelectorAll('.js-comment').length == 0
      )
  }

  clearCache() {
    const replyTo = this.newReplyTarget.parentElement.closest('.js-comment')
    delete commentReplyCache[replyTo.dataset.commentId]
  }

  setCache(replyTo) {
    if (replyTo) {
      const editor = replyTo.querySelector('.tiptap [contenteditable]')
      if (editor.textContent.trim() !== '') {
        commentReplyCache[replyTo.dataset.commentId] = replyTo.querySelector(
          '.tiptap [contenteditable]'
        ).innerHTML
      }
    }
  }

  getSelection() {
    const selectionRange = window.getSelection()

    if (selectionRange.type !== 'None') {
      const parentElement =
        selectionRange.getRangeAt(0).endContainer.parentElement

      // The selection should be from a comment or post itself
      if (
        parentElement.closest('.js-comment') ||
        parentElement.closest('.js-post-text-content')
      ) {
        return selectionRange.toString()
      } else {
        return null
      }
    } else {
      return null
    }
  }

  async #scrollToHashedComment() {
    const commentDomId = parseSiteCommentHash(window.location.hash)
    if (!commentDomId) return

    // Usually the comment is already on the first page — scroll without waiting.
    if (this.#scrollToCommentIfPresent(commentDomId)) return

    if (!(await this.#paginatorReady())) return

    await seekAcrossPages({
      find: () => this.#scrollToCommentIfPresent(commentDomId),
      loadNext: () => this.#loadNextCommentPage(),
    })
  }

  // Stimulus connects us before the paginator nested inside us, so the first page
  // request has to wait for it. Bounded: if it never connects there's nothing to page.
  async #paginatorReady() {
    for (let frame = 0; frame < 10; frame++) {
      if (this.hashSeekAborted) return false
      if (this.#paginatorController()) return true
      await new Promise(requestAnimationFrame)
    }

    return false
  }

  #paginatorController() {
    const el = this.element.querySelector(
      '[data-controller~="cursor-paginator"]'
    )

    return (
      el &&
      this.application.getControllerForElementAndIdentifier(
        el,
        'cursor-paginator'
      )
    )
  }

  #scrollToCommentIfPresent(commentDomId) {
    const el = document.getElementById(commentDomId)
    if (!el || !this.element.contains(el)) return false

    // Synchronously, so anything else scrolling to the hash also finds it visible
    revealCollapsedAncestors(el)

    requestAnimationFrame(() => {
      scrollIntoView(el, {
        // Jump rather than smooth-scroll: each comment passed on the way renders its
        // lazy attachments as it comes into view, pushing the target down mid-scroll.
        // Not 'auto': site themes set `scroll-behavior: smooth` on <html>.
        behavior: 'instant',
        scrollMode: 'always',
        block: 'center',
        boundary: getScrollParent(el),
      })
    })
    return true
  }

  // Asks the nested paginator for one more page. Resolves false when there's nothing
  // left to load, we've been disconnected, or the request never came back.
  #loadNextCommentPage() {
    const controller = this.#paginatorController()
    const paginator = controller?.element
    const hasMorePages = paginator?.querySelector(
      '[data-cursor-paginator-target="loaderContainer"]'
    )
    if (!hasMorePages || this.hashSeekAborted) return Promise.resolve(false)

    return new Promise((resolve) => {
      const finish = (loaded) => {
        clearTimeout(timeout)
        paginator.removeEventListener('cursor-paginator:loadComplete', onLoaded)
        resolve(loaded && !this.hashSeekAborted)
      }
      const onLoaded = () => finish(true)
      const timeout = setTimeout(() => finish(false), PAGE_LOAD_TIMEOUT)

      paginator.addEventListener('cursor-paginator:loadComplete', onLoaded)
      controller.loadNextPage()
    })
  }
}
